import { DealerConfig } from './dealerAuth';
import { logger } from './logger';

/**
 * Cache manager for dealer configuration with localStorage persistence
 */
export class DealerCache {
  private static config: DealerConfig | null = null;
  private static lastAuthTime: number = 0;
  // Dealer configuration can change during active debugging (e.g., enabling streaming),
  // so keep this TTL relatively short.
  private static readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour
  private static isLoading: boolean = false;
  private static readonly STORAGE_KEY = 'dealer_config_cache';
  private static readonly STORAGE_TIME_KEY = 'dealer_config_cache_time';
  private static readonly CACHE_VERSION = '3'; // Increment to invalidate old caches
  private static readonly CACHE_VERSION_KEY = 'dealer_config_cache_version';

  /**
   * Initialize cache from localStorage on first access
   */
  private static initializeFromStorage(): void {
    if (this.config !== null) return; // Already initialized
    
    try {
      const storedVersion = localStorage.getItem(this.CACHE_VERSION_KEY);
      const storedConfig = localStorage.getItem(this.STORAGE_KEY);
      const storedTime = localStorage.getItem(this.STORAGE_TIME_KEY);
      
      // Clear cache if version doesn't match
      if (storedVersion !== this.CACHE_VERSION) {
        logger.log('🔄 Cache version mismatch - clearing old cache');
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.removeItem(this.STORAGE_TIME_KEY);
        localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION);
        return;
      }
      
      if (storedConfig && storedTime) {
        const cacheTime = parseInt(storedTime, 10);
        if (Date.now() - cacheTime < this.CACHE_DURATION) {
          this.config = JSON.parse(storedConfig);
          this.lastAuthTime = cacheTime;
          logger.log('💾 Restored from cache with certification:', this.config?.certification_name);
        } else {
          // Clear expired cache from storage
          localStorage.removeItem(this.STORAGE_KEY);
          localStorage.removeItem(this.STORAGE_TIME_KEY);
          logger.log('🗑️ Cleared expired cache from localStorage');
        }
      }
    } catch (error) {
      logger.warn('⚠️ Failed to restore cache from localStorage:', error);
      // Clear potentially corrupted data
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIME_KEY);
      localStorage.removeItem(this.CACHE_VERSION_KEY);
    }
  }

  /**
   * Save cache to localStorage
   */
  private static saveToStorage(): void {
    if (!this.config) return;
    
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.config));
      localStorage.setItem(this.STORAGE_TIME_KEY, this.lastAuthTime.toString());
      localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION);
    } catch (error) {
      logger.warn('⚠️ Failed to save cache to localStorage:', error);
    }
  }

  /**
   * Get cached dealer configuration if valid
   */
  static get(): DealerConfig | null {
    // Initialize from storage on first access
    this.initializeFromStorage();
    
    if (!this.config) return null;
    
    if (Date.now() - this.lastAuthTime >= this.CACHE_DURATION) {
      logger.log('⏰ Dealer cache expired');
      this.clear(); // Clear both memory and storage
      return null;
    }

    // Using cached dealer config
    logger.log('💾 DealerCache: Returning cached config with certification:', this.config?.certification_name);
    return this.config;
  }

  /**
   * Set dealer configuration in cache
   */
  static set(config: DealerConfig): void {
    logger.log('💾 DealerCache: Setting config:', config);
    logger.log('💾 DealerCache: Certification name in config:', config?.certification_name);
    
    this.config = config;
    this.lastAuthTime = Date.now();
    this.saveToStorage();
    // Dealer config cached
  }

  /**
   * Clear cached configuration
   */
  static clear(): void {
    logger.log('🗑️ Clearing dealer cache');
    this.config = null;
    this.lastAuthTime = 0;
    this.isLoading = false;
    
    // Clear localStorage as well
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIME_KEY);
      localStorage.removeItem(this.CACHE_VERSION_KEY);
    } catch (error) {
      logger.warn('⚠️ Failed to clear localStorage:', error);
    }
  }

  /**
   * Check if cache is valid
   */
  static isValid(): boolean {
    return this.config !== null && (Date.now() - this.lastAuthTime < this.CACHE_DURATION);
  }

  /**
   * Check if currently loading
   */
  static isCurrentlyLoading(): boolean {
    return this.isLoading;
  }

  /**
   * Set loading state
   */
  static setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  /**
   * Wait for loading to complete
   */
  static async waitForLoad(): Promise<DealerConfig | null> {
    let waitTime = 0;
    const maxWaitTime = 10000; // 10 seconds
    
    while (this.isLoading && waitTime < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, 500));
      waitTime += 500;
    }
    
    if (waitTime >= maxWaitTime) {
      logger.error('❌ Cache loading timeout - clearing state');
      this.isLoading = false;
    }
    
    return this.config;
  }

  /**
   * Get cache statistics for debugging
   */
  static getStats(): {
    hasConfig: boolean;
    isValid: boolean;
    age: number;
    isLoading: boolean;
  } {
    return {
      hasConfig: this.config !== null,
      isValid: this.isValid(),
      age: this.config ? Date.now() - this.lastAuthTime : 0,
      isLoading: this.isLoading
    };
  }

  /**
   * Force refresh the cache (clear and re-authenticate on next access)
   */
  static forceRefresh(): void {
    logger.log('🔄 Forcing cache refresh');
    this.clear();
  }
}